import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DIFFERENT_LANGUAGE_AGREEMENT_ON_ORDER_DATA_PROCESSING_URL,
    DIFFERENT_LANGUAGE_PRICES_URL,
    DIFFERENT_LANGUAGE_PRIVACY_POLICY_URL,
    DIFFERENT_LANGUAGE_TERMS_AND_CONDITIONS_URL,
    FIRST_ELEMENT_INDEX,
    GERMAN_AGREEMENT_ON_ORDER_DATA_PROCESSING_URL,
    GERMAN_PRICES_URL,
    GERMAN_PRIVACY_POLICY_URL,
    GERMAN_TERMS_AND_CONDITIONS_URL,
    LANGUAGES,
} from '@modeso/aumico-lib-core-fe';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe/provider/organisation.provider';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { signup } from '../../actions/auth.actions';
import { localizationKeys } from '../../constants/localizationKeys';
import { SignupHelper } from '../../helpers/signup.helper';
import { IRegistration } from '../../interfaces/registration.interface';
import { AuthProvider } from '../../provider/auth.provider';
@UntilDestroy()
@Component({
    selector: 'aumico-users-quick-sign-up-page',
    templateUrl: './quick-sign-up.page.html',
    styleUrls: ['./sign-up.page.scss'],
})
export class QuickSignUpPage implements OnInit {
    isRegisterFormValid = false;
    registrationFormValue: IRegistration | undefined;
    backendError = false;
    isCreateAccountButtonClicked = false;
    localizations = localizationKeys;
    userEmailAlreadyExist: boolean;
    private browserLanguage: string | undefined;

    @Input() set supportedLocales(locales: string[]) {
        // Always use the language from the URL instead of browser language
        const urlLanguage = this.getLanguageFromUrl();

        if (urlLanguage) {
            // Check if the URL language is directly supported
            const isUrlLanguageSupported = locales.find((language) => language === urlLanguage);

            // Check if the base language (e.g., 'de' from 'de-ch') is supported
            const urlBaseLanguage = urlLanguage.split('-')[FIRST_ELEMENT_INDEX];
            const isUrlBaseLanguageSupported = locales.find(
                (language) => language.split('-')[FIRST_ELEMENT_INDEX] === urlBaseLanguage,
            );

            if (isUrlLanguageSupported != null) {
                this.locale = urlLanguage;
            } else if (isUrlBaseLanguageSupported != null) {
                this.locale = isUrlBaseLanguageSupported;
            } else {
                this.locale = this.defaultLanguage;
            }
        } else {
            this.locale = this.defaultLanguage;
        }
    }

    @Input() countries: INameId[] = [];
    @Input() defaultLanguage = LANGUAGES.ENGLISH;
    @Output() isPersonalInformationStep = new EventEmitter<boolean>();
    @Output() signUpSuccess = new EventEmitter<string>();
    @Output() onPrivacyPolicyClick = new EventEmitter<void>();
    locale: string;
    referralId: string = '';
    constructor(
        private readonly provider: AuthProvider,
        private readonly actionsListener$: ActionsSubject,
        private readonly domSanitizer: DomSanitizer,
        private readonly matIconRegistry: MatIconRegistry,
        private readonly UserProvider: OrganizationProvider,
        private route: ActivatedRoute,
        private router: Router,
        private i18NextService: I18NextService,
    ) {
        this.addSvgIcon('aumico-pattern-icon-loading');
        this.browserLanguage = navigator.language.toLowerCase();
    }

    private getLanguageFromUrl(): string | null {
        const urlSegments = this.router.url.split('/');
        const languageSegment = urlSegments[1]; // Language is typically the first segment after the base URL
        return languageSegment || null;
    }

    ngOnInit(): void {
        this.referralId = this.route.snapshot.queryParams['referral-uuid'];
        this.actionsListener$
            .pipe(ofType(signup.success), untilDestroyed(this))
            .subscribe(() => this.onSignUpSuccessfully());

        this.actionsListener$.pipe(ofType(signup.failure), untilDestroyed(this)).subscribe((action) => {
            const error = action.payload;
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            if (error != null && error.status === 409) {
                this.userEmailAlreadyExist = true;
            } else {
                this.onSignUpFail();
            }
        });
    }

    onRegisterFormStatusChange(registerFormStatus: boolean): void {
        this.isRegisterFormValid = registerFormStatus;
    }

    onRegisterFormValueChange(registerFormValue: IRegistration): void {
        this.registrationFormValue = registerFormValue;
    }

    public openPricesLink(): void {
        if (this.locale.split('-')[FIRST_ELEMENT_INDEX] !== LANGUAGES.SWISS_GERMAN.split('-')[FIRST_ELEMENT_INDEX]) {
            const locale = this.locale.split('-')[FIRST_ELEMENT_INDEX];
            const pricesUrl = this.replaceLocaleInUrl(DIFFERENT_LANGUAGE_PRICES_URL, locale);
            window.open(pricesUrl);
        } else {
            window.open(GERMAN_PRICES_URL);
        }
    }

    public openTermsAndConditionsLink(): void {
        if (this.locale.split('-')[FIRST_ELEMENT_INDEX] !== LANGUAGES.SWISS_GERMAN.split('-')[FIRST_ELEMENT_INDEX]) {
            const locale = this.locale.split('-')[FIRST_ELEMENT_INDEX];
            const termsAndConditionsUrl = this.replaceLocaleInUrl(DIFFERENT_LANGUAGE_TERMS_AND_CONDITIONS_URL, locale);
            window.open(termsAndConditionsUrl);
        } else {
            window.open(GERMAN_TERMS_AND_CONDITIONS_URL);
        }
    }

    public openPrivacyPolicyLink(): void {
        if (this.locale.split('-')[FIRST_ELEMENT_INDEX] !== LANGUAGES.SWISS_GERMAN.split('-')[FIRST_ELEMENT_INDEX]) {
            const locale = this.locale.split('-')[FIRST_ELEMENT_INDEX];
            const privacyPolicyUrl = this.replaceLocaleInUrl(DIFFERENT_LANGUAGE_PRIVACY_POLICY_URL, locale);
            window.open(privacyPolicyUrl);
        } else {
            window.open(GERMAN_PRIVACY_POLICY_URL);
        }
    }

    public openAgreementOnOrderDataProcessingLink(): void {
        if (this.locale.split('-')[FIRST_ELEMENT_INDEX] !== LANGUAGES.SWISS_GERMAN.split('-')[FIRST_ELEMENT_INDEX]) {
            const locale = this.locale.split('-')[FIRST_ELEMENT_INDEX];
            const agreementOnOrderDataProcessingUrl = this.replaceLocaleInUrl(
                DIFFERENT_LANGUAGE_AGREEMENT_ON_ORDER_DATA_PROCESSING_URL,
                locale,
            );
            window.open(agreementOnOrderDataProcessingUrl);
        } else {
            window.open(GERMAN_AGREEMENT_ON_ORDER_DATA_PROCESSING_URL);
        }
    }

    private replaceLocaleInUrl(url: string, locale: string): string {
        return url.replace('{locale}', locale);
    }

    createAccount(): void {
        this.isCreateAccountButtonClicked = true;
        if (this.registrationFormValue != null) {
            this.backendError = false;
            this.userEmailAlreadyExist = false;
            const signUpRequest = SignupHelper.prepareSignupRequestDataForQuickSignup(
                this.locale,
                this.registrationFormValue,
            );
            this.provider.dispatchSignUp(signUpRequest);
        } else {
            throw new Error('Register form is not complete!');
        }
    }

    private onSignUpSuccessfully(): void {
        this.acceptReferralIfExist();
        const { email } = this.registrationFormValue;
        this.signUpSuccess.emit(email);
    }

    private acceptReferralIfExist() {
        if (this.referralId) {
            this.UserProvider.dispatchAcceptInviteReferrals(this.referralId.trim());
        }
    }
    private onSignUpFail(): void {
        this.backendError = true;
        this.isCreateAccountButtonClicked = false;
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}
